import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ConfirmModal } from '../ui/ConfirmModal';
import { getContacts, updateContactStatus, deleteContact } from '../../services/api';
import { toast } from 'sonner';
import { Mail, Trash2, Clock, Check, Reply, Eye, CheckSquare, Square } from 'lucide-react';

export const ContactsViewer = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, contact: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ isOpen: false, count: 0 });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await getContacts();
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (contact) => {
    try {
      await updateContactStatus(contact.id, 'read');
      toast.success('Message marked as read');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkAsReplied = async (contact) => {
    try {
      await updateContactStatus(contact.id, 'replied');
      toast.success('Message marked as replied');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMarkAsNew = async (contact) => {
    try {
      await updateContactStatus(contact.id, 'new');
      toast.success('Message marked as new');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteContact(deleteModal.contact.id);
      toast.success('Message deleted successfully');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Bulk Selection Handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredContacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredContacts.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Bulk Operations
  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedIds.map(id => deleteContact(id)));
      toast.success(`${selectedIds.length} message(s) deleted successfully`);
      setSelectedIds([]);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete messages');
    }
  };

  const handleBulkStatusChange = async (status) => {
    try {
      await Promise.all(selectedIds.map(id => updateContactStatus(id, status)));
      toast.success(`${selectedIds.length} message(s) marked as ${status}`);
      setSelectedIds([]);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to update messages');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'read': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'replied': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const filteredContacts = filter === 'all' 
    ? contacts 
    : contacts.filter(c => c.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-2xl text-orange-400 animate-pulse">Loading messages...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">Contact Messages</h1>
          <p className="text-gray-400 mt-2">View and manage contact form submissions</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-black/50 border-green-500/30 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-black border-green-500/30">
            <SelectItem value="all">All Messages</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="bg-gradient-to-r from-orange-500/20 to-green-500/20 border-orange-500/50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-orange-400" />
              <span className="text-white font-semibold">{selectedIds.length} message(s) selected</span>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handleBulkStatusChange('new')}
                size="sm"
                className="bg-green-500 hover:bg-green-600"
              >
                <Check className="w-4 h-4 mr-1" />
                Mark as New
              </Button>
              <Button
                onClick={() => handleBulkStatusChange('read')}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Eye className="w-4 h-4 mr-1" />
                Mark as Read
              </Button>
              <Button
                onClick={() => handleBulkStatusChange('replied')}
                size="sm"
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Reply className="w-4 h-4 mr-1" />
                Mark as Replied
              </Button>
              <Button
                onClick={() => setBulkDeleteModal({ isOpen: true, count: selectedIds.length })}
                size="sm"
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Selected
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Select All Checkbox */}
      {filteredContacts.length > 0 && (
        <div className="flex items-center gap-3 px-2">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
          >
            {selectedIds.length === filteredContacts.length ? (
              <CheckSquare className="w-5 h-5 text-orange-400" />
            ) : (
              <Square className="w-5 h-5" />
            )}
            <span className="text-sm">Select All ({filteredContacts.length})</span>
          </button>
        </div>
      )}

      <div className="space-y-4">
        {filteredContacts.length === 0 ? (
          <Card className="bg-black/50 border-gray-500/30 p-12 text-center">
            <Mail className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No contact messages found</p>
          </Card>
        ) : (
          filteredContacts.map((contact) => (
            <Card key={contact.id} className="bg-black/50 border-orange-500/30 hover:border-orange-500 transition-colors p-6">
              <div className="flex gap-4">
                {/* Checkbox */}
                <div className="flex items-start pt-1">
                  <button
                    onClick={() => toggleSelect(contact.id)}
                    className="hover:scale-110 transition-transform"
                  >
                    {selectedIds.includes(contact.id) ? (
                      <CheckSquare className="w-6 h-6 text-orange-400" />
                    ) : (
                      <Square className="w-6 h-6 text-gray-500 hover:text-gray-300" />
                    )}
                  </button>
                </div>

                {/* Message Content */}
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{contact.name}</h3>
                        <Badge className={getStatusColor(contact.status)}>
                          {contact.status}
                        </Badge>
                      </div>
                      <div className="flex items-center text-gray-400 text-sm mb-2">
                        <Mail className="w-4 h-4 mr-2" />
                        <a href={`mailto:${contact.email}`} className="hover:text-orange-400 transition-colors">
                          {contact.email}
                        </a>
                      </div>
                      <div className="flex items-center text-gray-500 text-xs">
                        <Clock className="w-4 h-4 mr-2" />
                        {new Date(contact.created_at).toLocaleString()}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      {contact.status !== 'read' && (
                        <button
                          onClick={() => handleMarkAsRead(contact)}
                          className="p-2 text-orange-400 hover:bg-orange-500/20 rounded-lg transition-colors border border-orange-500/30 hover:border-orange-500"
                          title="Mark as Read"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      {contact.status !== 'replied' && (
                        <button
                          onClick={() => handleMarkAsReplied(contact)}
                          className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors border border-blue-500/30 hover:border-blue-500"
                          title="Mark as Replied"
                        >
                          <Reply className="w-4 h-4" />
                        </button>
                      )}
                      {contact.status !== 'new' && (
                        <button
                          onClick={() => handleMarkAsNew(contact)}
                          className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition-colors border border-green-500/30 hover:border-green-500"
                          title="Mark as New"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteModal({ isOpen: true, contact })}
                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/30 hover:border-red-500"
                        title="Delete Message"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="bg-black/30 rounded p-4 border border-gray-700">
                    <p className="text-gray-300 whitespace-pre-wrap">{contact.message}</p>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Single Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, contact: null })}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the message from ${deleteModal.contact?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={bulkDeleteModal.isOpen}
        onClose={() => setBulkDeleteModal({ isOpen: false, count: 0 })}
        onConfirm={handleBulkDelete}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${bulkDeleteModal.count} selected message(s)? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
      />
    </div>
  );
};
